import type { TrainingBlock, TrainingSession, TrainingWeek } from '@cadencia/contracts';
import { canRebuildPreparedWeek } from '@cadencia/domain';
import { startOfWeek } from '../../lib/dates.js';
import { logger } from '../../lib/logger.js';
import { db } from '../db.js';
import { listCardioOfWeek } from '../repositories/cardioRepo.js';
import {
  ensureWeekMaterialized,
  getActiveBlock,
  getSessionDetail,
  listSessionsOfWeek,
  listWarmups,
  listWeeks,
} from '../repositories/trainingRepo.js';
import { softDeleteRow, type WriteContext } from '../writes.js';

/**
 * La semana preparada de antemano: encontrarla, retirarla y rehacerla.
 *
 * Existe por la mitad de la decisión del 13/09/2026 que protege ADR-025: una
 * semana se construye con los `volumeTargets` y las prioridades que el bloque
 * tiene ESE día. Prepararla el domingo la congelaría, y un «+2 series de
 * pectoral» aprobado el lunes solo cambiaría el bloque. Así que, mientras nadie
 * haya entrenado en ella, se rehace.
 *
 * Va aparte de `weekCloseService` a propósito: `applyDecisionsService` la
 * necesita, y la revisión semanal —que usa `applyDecisions`— la necesita
 * `weekCloseService`. Juntas harían un ciclo de imports.
 */

export type ResultadoSemanaPreparada = 'none' | 'started' | 'discarded' | 'rebuilt';

/** La semana activa POSTERIOR a la de hoy: solo existe si se preparó antes de tiempo. */
export async function preparedWeekOf(
  block: TrainingBlock,
  today: string,
): Promise<TrainingWeek | undefined> {
  const lunesDeHoy = startOfWeek(today);
  return (await listWeeks(block.id)).find(
    (semana) => semana.status === 'active' && semana.weekStart > lunesDeHoy,
  );
}

/**
 * Borra (con lápida) todo lo que la materialización creó para esas sesiones:
 * ejercicios, series prescritas, aproximaciones y el cardio de la semana que
 * no se haya registrado. Con lápida y no a secas, para que el otro dispositivo
 * también lo pierda.
 */
async function retirar(
  ctx: WriteContext,
  semana: TrainingWeek,
  sesiones: readonly TrainingSession[],
): Promise<void> {
  for (const sesion of sesiones) {
    const detalle = await getSessionDetail(sesion.id);
    for (const item of detalle?.exercises ?? []) {
      for (const aproximacion of await listWarmups(item.sessionExercise.id)) {
        await softDeleteRow(ctx, 'warmupPrescriptions', aproximacion.id);
      }
      for (const prescripcion of item.prescriptions) {
        await softDeleteRow(ctx, 'setPrescriptions', prescripcion.id);
      }
      await softDeleteRow(ctx, 'sessionExercises', item.sessionExercise.id);
    }
    await softDeleteRow(ctx, 'trainingSessions', sesion.id);
  }

  // El cardio no cuelga de la sesión (`sessionId` es null lo normal): se
  // retira el de las fechas de la semana. Uno que ya tenga registro se deja,
  // aunque en una semana intacta y por venir no debería haberlo.
  const conRegistro = new Set(
    (await db.cardioLogs.where('userId').equals(ctx.userId).toArray())
      .filter((registro) => registro.deletedAt === null)
      .map((registro) => registro.prescriptionId),
  );
  for (const cardio of await listCardioOfWeek(ctx.userId, semana.weekStart)) {
    if (!conRegistro.has(cardio.id)) await softDeleteRow(ctx, 'cardioPrescriptions', cardio.id);
  }
}

/**
 * Rehace —o solo retira— la semana preparada de antemano, si sigue intacta.
 *
 * - `rebuild`: tras un cambio de volumen o de prioridades. Se retira y se vuelve
 *   a materializar con el bloque como está ahora.
 * - `discard`: antes de un cambio de fase. Las sesiones de esa semana las pondrá
 *   el bloque nuevo; dejar las del viejo daría dos planes el mismo día.
 *
 * `block` se pasa en el cambio de fase porque, cuando se aplica, el activo deja
 * de ser el viejo.
 */
export async function rehacerSemanaPreparada(
  ctx: WriteContext,
  today: string,
  modo: 'rebuild' | 'discard',
  block?: TrainingBlock,
): Promise<ResultadoSemanaPreparada> {
  const bloque = block ?? (await getActiveBlock(ctx.userId));
  if (bloque === undefined) return 'none';

  const semana = await preparedWeekOf(bloque, today);
  if (semana === undefined) return 'none';

  const sesiones = await listSessionsOfWeek(semana.id);
  if (!canRebuildPreparedWeek(sesiones, today)) {
    logger.info('engine', 'PREPARED_WEEK_KEPT', 'La semana preparada ya se está viviendo: no se rehace', {
      weekIndex: semana.weekIndex,
      modo,
    });
    return 'started';
  }

  await retirar(ctx, semana, sesiones);
  if (modo === 'discard') return 'discarded';

  await ensureWeekMaterialized(ctx, semana.weekStart);
  logger.info('engine', 'PREPARED_WEEK_REBUILT', 'Semana preparada rehecha con el bloque actual', {
    weekIndex: semana.weekIndex,
  });
  return 'rebuilt';
}
